import {
  ExtranetContainer,
  ExtranetModal,
  ExtranetModalBody,
  ExtranetModalHeader,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacButton, PharmacFieldset, PharmacText } from '@pharmac/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Control, FieldValues, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { __PRIVACY_POLICY_LINK, __TERMS_OF_USE_LINK } from '../../constants'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'
import { extranetUsersQueryKeyFactory } from '../../features/users/users.queries'
import { useExtranetPath } from '../../router/useExtranetPath'
import { api } from '../../services/HttpService'
import { ExtranetGradient } from '../extranet/ExtranetGradient'
import { ApprovalLink } from './ApprovalLink'
import { ApprovalSwitch } from './ApprovalSwitch'

type UserApprovalModalForm = {
  marketingAuthorization: boolean
  privacyPolicyApproval: boolean
  termsOfUseApproval: boolean
}

export const UserApprovalModal = () => {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const createLink = useExtranetPath()
  const { user, doLogout } = useAuthenticationContext()
  const { minDesktop, minTablet, maxMobile } = useBreakpoint()

  const formHook = useForm<UserApprovalModalForm>({
    defaultValues: {
      marketingAuthorization: !!user?.marketingAuthorizationDate,
      privacyPolicyApproval: !!user?.privacyPolicyApprovalDate,
      termsOfUseApproval: !!user?.extranetTermsOfUseApprovalDate,
    },
  })

  const queryClient = useQueryClient()

  const { mutate } = useMutation({
    mutationFn: async (values: UserApprovalModalForm) => {
      await api.users.updateUserPrivacyApproval({ approval: values.privacyPolicyApproval })
      await api.users.updateUserMarketingApproval({ approval: values.marketingAuthorization })
      await api.users.updateUserTermsOfServiceApproval({ approval: values.termsOfUseApproval })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: extranetUsersQueryKeyFactory.getCurrentUser() })

      navigate(createLink('root'))
    },
  })

  const handleSubmit = (values: UserApprovalModalForm) => {
    mutate(values)
  }

  const privacyPolicyApproval = formHook.watch('privacyPolicyApproval')
  const termsOfUseApproval = formHook.watch('termsOfUseApproval')

  const buttonIsDisabled = !privacyPolicyApproval || !termsOfUseApproval

  return (
    <ExtranetGradient>
      <ExtranetModal
        preventClose
        style={{
          backgroundColor: 'var(--color-accent-2)',
        }}
        buttons={[
          <PharmacButton
            onClick={() => doLogout()}
            variant="secondary"
          >
            {t('common:logout')}
          </PharmacButton>,
          <PharmacButton
            onClick={formHook.handleSubmit(handleSubmit)}
            variant="primary"
            disabled={buttonIsDisabled}
          >
            {t('common:accept')}
          </PharmacButton>,
        ]}
      >
        <ExtranetModalHeader
          style={{
            fontSize: maxMobile ? 'var(--typo-h6-size)' : 'var(--typo-h5-size)',
            color: 'var(--color-primary)',
            padding: maxMobile ? '0 0' : 'var(--spacing-medium) 0',
            textAlign: 'center',
          }}
        >
          {t('extranet:user.approvalModalTitle')}
        </ExtranetModalHeader>
        <ExtranetModalBody style={{ padding: maxMobile ? 'var(--spacing-medium) 0' : undefined }}>
          <ExtranetContainer
            paddingMediumHorizontal={minTablet}
            full
            flexColumn
            style={{
              overflowY: 'auto',
              overflowX: 'hidden',
              scrollbarWidth: 'thin',
              scrollbarColor: 'var(--color-primary) var(--color-accent-2)',
              backgroundColor: 'var(--color-accent-2)',
              borderRadius: '18px',
            }}
          >
            <PharmacFieldset
              paddingSmallVertical={maxMobile}
              paddingMediumHorizontal={maxMobile}
            >
              <ExtranetContainer
                paddingLargeTop={!maxMobile}
                paddingLargeLeft={minDesktop}
                flexColumn
                full
              >
                <ApprovalSwitch
                  name="marketingAuthorization"
                  control={formHook.control as unknown as Control<FieldValues>}
                  label={t('extranet:user.marketingAuthorization')}
                  marginLargeBottom={!maxMobile}
                  marginSmallBottom={maxMobile}
                >
                  <PharmacText paddingSmallTop>
                    {t('extranet:inviteLinks.registration.marketingAuthorizationInfo')}
                  </PharmacText>
                </ApprovalSwitch>
                <ApprovalSwitch
                  rules={{ required: true }}
                  label={`${t('extranet:user.privacyPolicy')}*`}
                  name="privacyPolicyApproval"
                  control={formHook.control as unknown as Control<FieldValues>}
                  marginLargeBottom={!maxMobile}
                  marginSmallBottom={maxMobile}
                >
                  <PharmacText
                    paddingSmallTop
                    paddingMediumBottom
                  >
                    {t('extranet:inviteLinks.registration.privacyPolicyInfo')}
                  </PharmacText>
                  <ApprovalLink
                    url={__PRIVACY_POLICY_LINK}
                    text={t('extranet:inviteLinks.registration.extranetPrivacyPolicy')}
                  />
                </ApprovalSwitch>
                <ApprovalSwitch
                  rules={{ required: true }}
                  label={`${t('extranet:user.termsOfUse')}*`}
                  name="termsOfUseApproval"
                  control={formHook.control as unknown as Control<FieldValues>}
                  marginLargeBottom={!maxMobile}
                  marginSmallBottom={maxMobile}
                >
                  <PharmacText
                    paddingSmallTop
                    paddingMediumBottom
                  >
                    {t('extranet:user.termsOfUseInfo')}
                  </PharmacText>
                  <ApprovalLink
                    url={__TERMS_OF_USE_LINK}
                    text={t('extranet:inviteLinks.registration.extranettermsOfUse')}
                  />
                </ApprovalSwitch>
              </ExtranetContainer>
            </PharmacFieldset>
          </ExtranetContainer>
        </ExtranetModalBody>
      </ExtranetModal>
    </ExtranetGradient>
  )
}
